import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DISCIPLINES, GENDERS, FORMATS, PLAYOFF_FORMATS, SKILLS } from "@/lib/tournament";

const inputCls = "w-full rounded-xl bg-surface glow-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400";
const SKILL_PRESETS = ["Open", "Beginner", "Low-Intermediate", "Intermediate", "Advanced", "2.5", "3.0", "3.5", "4.0", "4.5", "5.0+"];

function Dd({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="rounded-xl glow-border bg-surface" style={{ color: "var(--text)" }}><SelectValue /></SelectTrigger>
      <SelectContent className="glass" style={{ background: "var(--surface-glass)" }}>
        {options.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`${testid}-${o.v}`}>{o.l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

// Playoff winning-score presets. Pool matches always play to 11 (win by 2).
export const PLAYOFF_SCORING_PRESETS = [
  { v: "same", l: "Same as pool — to 11, win by 2", s: { pool_target: 11, pool_win_by: 2, pool_cap: 0, playoff_target: 11, playoff_win_by: 2, playoff_cap: 0 } },
  { v: "15", l: "Playoffs to 15 (win by 2)", s: { pool_target: 11, pool_win_by: 2, pool_cap: 0, playoff_target: 15, playoff_win_by: 2, playoff_cap: 0 } },
  { v: "21", l: "Playoffs to 21 (win by 2)", s: { pool_target: 11, pool_win_by: 2, pool_cap: 0, playoff_target: 21, playoff_win_by: 2, playoff_cap: 0 } },
  { v: "15_21", l: "Playoffs 15, win by 2, cap at 21", s: { pool_target: 11, pool_win_by: 2, pool_cap: 0, playoff_target: 15, playoff_win_by: 2, playoff_cap: 21 } },
];

export function scoringToPreset(sc) {  if (!sc) return "same";
  const match = PLAYOFF_SCORING_PRESETS.find(
    (p) => p.s.playoff_target === sc.playoff_target && p.s.playoff_win_by === sc.playoff_win_by && (p.s.playoff_cap || 0) === (sc.playoff_cap || 0)
  );
  return match ? match.v : "same";
}

// short label for a category's playoff scoring rule (for list rows)
export function scoringLabel(sc) {
  if (!sc || (sc.playoff_target || 11) === 11) return null;
  return sc.playoff_cap ? `playoffs to ${sc.playoff_target}, cap ${sc.playoff_cap}` : `playoffs to ${sc.playoff_target}`;
}

// Shared category builder fields: discipline, custom skill tier, gender, format, pools.
export default function CategoryDraftFields({ draft, setDraft, idPrefix = "draft" }) {
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  return (
    <div className="space-y-3" data-testid={`${idPrefix}-fields`}>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      <div><label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Discipline</label><div className="mt-1"><Dd testid={`${idPrefix}-discipline`} value={draft.discipline} onChange={(v) => set({ discipline: v })} options={DISCIPLINES} /></div></div>
      <div>
        <label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Skill tier</label>
        <input data-testid={`${idPrefix}-skill`} list={`${idPrefix}-skill-presets`} value={draft.skill_level === "open" ? "Open" : (draft.skill_level || "")}
          onChange={(e) => set({ skill_level: e.target.value })} placeholder="e.g. Beginner" className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} />
        <datalist id={`${idPrefix}-skill-presets`}>{SKILL_PRESETS.map((s) => <option key={s} value={s} />)}</datalist>
      </div>
      <div><label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Division</label><div className="mt-1"><Dd testid={`${idPrefix}-gender`} value={draft.gender} onChange={(v) => set({ gender: v })} options={GENDERS} /></div></div>
      <div><label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Stage format</label><div className="mt-1"><Dd testid={`${idPrefix}-format`} value={draft.format} onChange={(v) => set({ format: v })} options={FORMATS} /></div></div>
      <div><label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Playoff format</label><div className="mt-1"><Dd testid={`${idPrefix}-playoff-format`} value={draft.playoff_format || "single_elim"} onChange={(v) => set({ playoff_format: v })} options={PLAYOFF_FORMATS} /></div></div>
      <div>
        <label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Brackets / pools</label>
        <input data-testid={`${idPrefix}-pools`} type="number" min="1" max="8" value={draft.pools || 1}
          onChange={(e) => set({ pools: Math.max(1, Math.min(8, Number(e.target.value) || 1)) })} className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} />
      </div>
      <div>
        <label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Max entries</label>
        <input data-testid={`${idPrefix}-max-entries`} type="number" min="1" max="1024" value={draft.max_entries || ""} placeholder="Unlimited"
          onChange={(e) => { const n = parseInt(e.target.value, 10); set({ max_entries: n > 0 ? Math.min(1024, n) : null }); }} className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} />
        <p className="text-[10px] mt-1" style={{ color: "var(--text-2)" }}>A doubles pair = 1 entry. Extra sign-ups join a waitlist.</p>
      </div>
      <div>
        <label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Playoff scoring</label>
        <div className="mt-1">
          <Dd testid={`${idPrefix}-playoff-scoring`}
            value={scoringToPreset(draft.scoring)}
            onChange={(v) => set({ scoring: (PLAYOFF_SCORING_PRESETS.find((p) => p.v === v) || PLAYOFF_SCORING_PRESETS[0]).s })}
            options={PLAYOFF_SCORING_PRESETS} />
        </div>
      </div>
      </div>
    </div>
  );
}

export const DEFAULT_DRAFT = { discipline: "doubles", skill_level: "open", gender: "mixed", format: "single_elim", playoff_format: "single_elim", pools: 1, consolation: false, scoring: PLAYOFF_SCORING_PRESETS[0].s };
export { SKILLS };
